import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { GlobalSearch } from './GlobalSearch';
import { NotificationsBell } from './NotificationsBell';
import { FeedbackDialog } from './FeedbackButton';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  LayoutDashboard,
  BookOpen,
  MessageSquare,
  Users,
  GraduationCap,
  Building2,
  Settings,
  LogOut,
  Menu,
  X,
  Bell,
  ChevronDown,
  KeyRound,
  Wrench,
  Briefcase,
  FileText,
  Image as ImageIcon,
  Accessibility,
  Sparkles,
  MessageSquarePlus,
  LayoutTemplate,
  Newspaper,
  BookOpenCheck,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MainLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dragOverSection, setDragOverSection] = useState(null);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({ current: '', new: '', confirm: '' });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [sectionCounts, setSectionCounts] = useState({});

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const res = await axios.get(`${API}/sections/counts`);
        setSectionCounts(res.data);
      } catch (e) { /* badges are non-critical */ }
    };
    fetchCounts();
    window.addEventListener('folder-section-changed', fetchCounts);
    return () => window.removeEventListener('folder-section-changed', fetchCounts);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleChangePassword = async () => {
    setPasswordError('');
    if (!passwordData.current || !passwordData.new || !passwordData.confirm) {
      setPasswordError('All fields are required');
      return;
    }
    if (passwordData.new.length < 6) {
      setPasswordError('New password must be at least 6 characters');
      return;
    }
    if (passwordData.new !== passwordData.confirm) {
      setPasswordError('New passwords do not match');
      return;
    }
    setPasswordLoading(true);
    try {
      await axios.put(`${API}/auth/change-password`, {
        email: user?.email,
        current_password: passwordData.current,
        new_password: passwordData.new
      });
      toast.success('Password changed successfully!');
      setChangePasswordOpen(false);
      setPasswordData({ current: '', new: '', confirm: '' });
    } catch (err) {
      setPasswordError(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      system_admin: { label: 'System Admin', color: 'bg-[#34657f] text-white' },
      team_admin: { label: 'Program Administrator', color: 'bg-[#b34e00] text-white' },
      instructor: { label: 'Instructor', color: 'bg-[#3d7a22] text-white' },
      skills_trainer: { label: 'Skills Trainer', color: 'bg-[#00707b] text-white' },
      demo: { label: 'Demo · View only', color: 'bg-amber-700 text-white' },
    };
    return roleConfig[role] || { label: 'User', color: 'bg-slate-500 text-white' };
  };

  const isDemo = user?.role === 'demo';
  const isAdmin = user?.role === 'system_admin' || user?.role === 'team_admin';

  const SECTION_LABELS = {
    curriculum: 'Curriculum',
    work_based_learning: 'Work-Based Learning',
    toolkit: 'Toolkit Resources',
    training_files: 'Training Files',
  };

  const sectionItems = [
    { path: '/curriculum?section=curriculum', section: 'curriculum', label: 'Curriculum', icon: BookOpen },
    { path: '/curriculum?section=work_based_learning', section: 'work_based_learning', label: 'Work-Based Learning', icon: Briefcase },
    { path: '/curriculum?section=toolkit', section: 'toolkit', label: 'Toolkit Resources', icon: Wrench },
    { path: '/curriculum?section=training_files', section: 'training_files', label: 'Training Files', icon: FileText },
  ];

  const navItems = isDemo
    ? [
        ...sectionItems,
        { path: '/templates', label: 'Personalize Templates', icon: LayoutTemplate },
        { path: '/forum', label: 'Community Forum', icon: MessageSquare },
      ]
    : [
        { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
        ...sectionItems,
        { path: '/templates', label: 'Personalize Templates', icon: LayoutTemplate },
        { path: '/forum', label: 'Community Forum', icon: MessageSquare },
      ];

  navItems.push({ path: '/newsletter', label: 'Newsletter Builder', icon: Newspaper });

  // Intern Progress & Scoring Rubric — system admins only
  if (user?.role === 'system_admin') {
    navItems.push({ path: '/students', label: 'Intern Progress', icon: GraduationCap });
    navItems.push({ path: '/rubric', label: 'Scoring Rubric', icon: BookOpenCheck });
  }
  // User Management — all administrators
  if (user?.role === 'system_admin' || user?.role === 'team_admin') {
    navItems.push({ path: '/directory', label: 'Program Directory', icon: Building2 });
    navItems.push({ path: '/users', label: 'User Management', icon: Users });
  }

  const activeSection = new URLSearchParams(location.search).get('section');

  const handleSectionDrop = async (e, section) => {
    e.preventDefault();
    setDragOverSection(null);
    const folderId = e.dataTransfer.getData('text/plain');
    if (!folderId) return;
    try {
      await axios.put(`${API}/folders/${folderId}/section`, { section });
      toast.success(`Folder moved to ${SECTION_LABELS[section]}`);
      window.dispatchEvent(new CustomEvent('folder-section-changed'));
    } catch (err) {
      toast.error('Failed to move folder');
    }
  };

  const roleBadge = getRoleBadge(user?.role);

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="main-layout">
      {/* Skip to content link */}
      <a href="#main-content" className="skip-to-content">
        Skip to main content
      </a>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full w-64 flex flex-col bg-[#eaf1f5] border-r border-[#34657f]/15 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="sidebar"
      >
        {/* Sidebar Header */}
        <div className="h-20 shrink-0 flex items-center justify-between px-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <img
              src="/lifelink-logo.png"
              alt="LIFE Link"
              className="h-14 w-auto object-contain"
            />
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-slate-500 hover:text-slate-700"
            aria-label="Close menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 min-h-0 overflow-y-auto p-4 space-y-1" data-testid="sidebar-nav">
          {navItems.map((item) => {
            const isSectionItem = !!item.section;
            const sectionActive = isSectionItem &&
              location.pathname === '/curriculum' && activeSection === item.section;
            const isDropTarget = isSectionItem && isAdmin;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `sidebar-item ${(isSectionItem ? sectionActive : isActive) ? 'active' : ''} ${
                    dragOverSection === item.section ? 'ring-2 ring-[#e87722] ring-inset bg-[#e87722]/5' : ''
                  }`
                }
                data-testid={`nav-${item.section || item.path.slice(1)}`}
                onDragOver={isDropTarget ? (e) => { e.preventDefault(); setDragOverSection(item.section); } : undefined}
                onDragLeave={isDropTarget ? () => setDragOverSection(null) : undefined}
                onDrop={isDropTarget ? (e) => handleSectionDrop(e, item.section) : undefined}
              >
                <item.icon className="w-5 h-5" />
                <span className="flex-1">{item.label}</span>
                {isSectionItem && sectionCounts[item.section] > 0 && (
                  <span
                    className="text-[11px] font-semibold px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 shrink-0"
                    data-testid={`section-count-${item.section}`}
                  >
                    {sectionCounts[item.section]}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* Sidebar Footer */}
        <div className="shrink-0 p-4 border-t border-[#34657f]/15 bg-[#eaf1f5]">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10">
              <AvatarImage src={user?.avatar_url} alt={user?.name} />
              <AvatarFallback className="bg-[#34657f] text-white">
                {getInitials(user?.name || 'User')}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-[#1A1A1A] truncate">
                {user?.name}
              </p>
              <span className={`text-xs px-2 py-0.5 rounded-full ${roleBadge.color}`}>
                {roleBadge.label}
              </span>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Header */}
        <header className="sticky top-0 z-30 h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 lg:px-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-slate-600 hover:text-slate-900"
              aria-label="Open menu"
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-6 h-6" />
            </button>
            <h1 className="font-['Manrope'] text-xl font-bold text-[#1A1A1A] hidden sm:block">
              Welcome, {user?.name?.split(' ')[0]}
            </h1>
          </div>

          <div className="flex items-center gap-4">
            <GlobalSearch />
            <Button
              variant="outline"
              size="sm"
              onClick={() => setFeedbackOpen(true)}
              className="border-[#34657f] text-[#34657f] hover:bg-[#34657f] hover:text-white"
              aria-label="Send feedback"
              data-testid="header-feedback-btn"
            >
              <MessageSquarePlus className="w-4 h-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Feedback</span>
            </Button>
            <NotificationsBell />

            {/* User Menu */}
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  aria-label="Account menu"
                  className="flex items-center gap-2 px-2"
                  data-testid="user-menu-btn"
                >
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-[#34657f] text-white text-sm font-semibold">
                      {getInitials(user?.name || 'User')}
                    </AvatarFallback>
                  </Avatar>
                  <ChevronDown className="w-4 h-4 text-slate-500" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-2">
                  <p className="text-sm font-semibold">{user?.name}</p>
                  <p className="text-xs text-slate-500">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                {isAdmin && (
                  <DropdownMenuItem
                    onClick={() => navigate('/dashboard?manage=photos')}
                    className="cursor-pointer"
                    data-testid="manage-photos-btn"
                  >
                    <ImageIcon className="w-4 h-4 mr-2" />
                    Manage Photos
                  </DropdownMenuItem>
                )}
                {!isDemo && (
                  <DropdownMenuItem
                    onClick={() => navigate('/welcome')}
                    className="cursor-pointer"
                    data-testid="welcome-tour-menu-item"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Welcome Tour
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={() => setFeedbackOpen(true)}
                  className="cursor-pointer"
                  data-testid="feedback-menu-item"
                >
                  <MessageSquarePlus className="w-4 h-4 mr-2" />
                  Send Feedback
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setChangePasswordOpen(true)}
                  className="cursor-pointer"
                  data-testid="change-password-btn"
                >
                  <KeyRound className="w-4 h-4 mr-2" />
                  Change Password
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate('/settings')}
                  className="cursor-pointer"
                  data-testid="my-profile-btn"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  My Profile
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => window.open('/accessibility', '_blank', 'noopener')}
                  className="cursor-pointer"
                  data-testid="accessibility-menu-item"
                >
                  <Accessibility className="w-4 h-4 mr-2" />
                  Accessibility Statement
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="cursor-pointer text-red-600 focus:text-red-600"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <main id="main-content" tabIndex={-1} className="p-4 lg:p-8 outline-none">
          {children}
        </main>

        {/* Footer */}
        <footer className="px-4 lg:px-8 pb-6 pt-2" data-testid="portal-footer">
          <div className="border-t border-slate-200 pt-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-[#64707a]">
            <span>&copy; 2026 LIFE Link | Project LIFE | Butler Tech</span>
            <nav aria-label="Legal" className="flex items-center gap-4">
              <a href="/privacy" target="_blank" rel="noopener" className="underline hover:text-[#34657f] py-1.5" data-testid="footer-privacy-link">Privacy Policy</a>
              <a href="/terms" target="_blank" rel="noopener" className="underline hover:text-[#34657f] py-1.5" data-testid="footer-terms-link">Terms &amp; Conditions</a>
              <a href="/accessibility" target="_blank" rel="noopener" className="underline hover:text-[#34657f] py-1.5" data-testid="footer-accessibility-link">Accessibility</a>
            </nav>
          </div>
        </footer>

        <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />
      </div>

      {/* Change Password Dialog */}
      <Dialog open={changePasswordOpen} onOpenChange={(open) => {
        setChangePasswordOpen(open);
        if (!open) { setPasswordData({ current: '', new: '', confirm: '' }); setPasswordError(''); }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] text-xl">Change Password</DialogTitle>
            <DialogDescription>Enter your current password and choose a new one.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {passwordError && (
              <div className="bg-red-50 text-red-600 text-sm p-3 rounded-lg" data-testid="password-error">
                {passwordError}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="current-password">Current Password</Label>
              <Input
                id="current-password"
                type="password"
                value={passwordData.current}
                onChange={(e) => setPasswordData(prev => ({ ...prev, current: e.target.value }))}
                data-testid="current-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                value={passwordData.new}
                onChange={(e) => setPasswordData(prev => ({ ...prev, new: e.target.value }))}
                placeholder="At least 6 characters"
                data-testid="new-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm New Password</Label>
              <Input
                id="confirm-password"
                type="password"
                value={passwordData.confirm}
                onChange={(e) => setPasswordData(prev => ({ ...prev, confirm: e.target.value }))}
                onKeyDown={(e) => { if (e.key === 'Enter') handleChangePassword(); }}
                data-testid="confirm-password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setChangePasswordOpen(false)}>Cancel</Button>
            <Button
              onClick={handleChangePassword}
              disabled={passwordLoading}
              className="bg-[#34657f]"
              data-testid="submit-change-password-btn"
            >
              {passwordLoading ? 'Changing...' : 'Change Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MainLayout;
